import { useCallback } from "react";
import { useIntl } from "react-intl";
import commonMessages from "../lib/messages";

// RECORD_LOCKED is thrown (via HttpsError) by the ~40 backend handlers
// guarding merge-locked customers/patients/appointments (see kudzu-backend
// common/checkMergeStatus.ts) — handling it here gives every one of those
// call sites a readable message for free instead of the generic fallback.
const getKnownErrorMessage = (
  error: unknown,
  formatMessage: ReturnType<typeof useIntl>["formatMessage"],
): string | null => {
  const message = (error as { message?: string } | undefined)?.message;
  if (message === "RECORD_LOCKED") {
    return formatMessage(commonMessages.recordLocked);
  }
  return null;
};

const useDefaultErrorWrapper = () => {
  const { formatMessage } = useIntl();

  const runAsyncFunction = useCallback(
    async <R, A>(func: (args: A) => Promise<R>, args: A): Promise<R> => {
      try {
        return await func(args);
      } catch (error) {
        throw new Error(
          getKnownErrorMessage(error, formatMessage) ??
            formatMessage(commonMessages.defaultError),
        );
      }
    },
    [formatMessage],
  );

  return { runAsyncFunction };
};

export default useDefaultErrorWrapper;
